import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';
import get from 'lodash.get';

import { HpRespModuleContainerOld } from '../HpRespModuleContainerOld/HpRespModuleContainerOld';
import { HpRespFeaturedModuleItem } from './HpRespFeaturedModuleItem';
import styles from './HpRespFeaturedModule.scss';
import { trackFeaturedImpressions, trackFeaturedClick } from './helpers/FeaturedTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { getSrcString, getSrcSetString } from '../helpers/getSrcStrings';
import { SeoLink } from 'dibs-seo/exports/SeoLink';

const handleInitialDisplay = ({ inViewport, items, primaryItem }) => {
    if (primaryItem && inViewport) {
        trackFeaturedImpressions([primaryItem, ...items]);
    }
};

const HpRespFeaturedModuleComponent = props => {
    const featuredModule = get(props, 'homepage.featuredModule');

    if (!featuredModule) {
        return null;
    }

    const { items, primaryItem } = featuredModule;

    return (
        <HpRespModuleContainerOld addTopGap>
            <SharedInViewport
                stopWhenInViewport
                onInViewportChange={({ inViewport }) =>
                    handleInitialDisplay({ inViewport, items, primaryItem })
                }
            >
                <div className="rowFlex">
                    <div className={classnames(styles.primaryItem, 'colXs12 colLg6')}>
                        <SeoLink
                            className={styles.primaryLink}
                            linkData={primaryItem.linkData}
                            ariaLabel={primaryItem.cta}
                            onClick={() => trackFeaturedClick(primaryItem, 0)}
                        >
                            <img
                                className={styles.primaryImage}
                                src={getSrcString({ url: primaryItem.imageUrl })}
                                alt={primaryItem.title}
                                sizes="(max-width:768px) 95vw, 50vw"
                                srcSet={getSrcSetString({
                                    url: primaryItem.imageUrl,
                                    widths: [400, 640, 800, 960, 1200],
                                })}
                            />
                        </SeoLink>
                    </div>

                    <div className={classnames(styles.productItems, 'colXs12 colLg6')}>
                        <div className="rowFlex">
                            {items.map((item, index) => (
                                <HpRespFeaturedModuleItem
                                    key={item.cmsId}
                                    item={item}
                                    onClick={() => {
                                        /**
                                         * Index incremented by 1 because primaryItem is using `position: 1`
                                         */
                                        trackFeaturedClick(item, index + 1);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </SharedInViewport>
        </HpRespModuleContainerOld>
    );
};

HpRespFeaturedModuleComponent.propTypes = {
    homepage: PropTypes.object.isRequired,
};

export const HpRespFeaturedModule = createFragmentContainer(HpRespFeaturedModuleComponent, {
    homepage: graphql`
        fragment HpRespFeaturedModule_homepage on Homepage {
            featuredModule: module(type: "featured") {
                ... on FeaturedModule {
                    primaryItem {
                        cta
                        imageUrl
                        title
                        linkData {
                            # FeaturedTracking.js
                            path
                            ...SeoLink_linkData
                        }
                    }
                    items {
                        cmsId
                        cta
                        imageUrl
                        title
                        linkData {
                            # FeaturedTracking.js
                            path
                            ...SeoLink_linkData
                        }
                    }
                }
            }
        }
    `,
});
